import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { apiErrorMessage } from "../lib/api";

export default function RegisterPlayer() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ alias: "", email: "", password: "", ageConfirmed: false, ref: "" });
  const [busy, setBusy] = useState(false);
  const { registerPlayer } = useAuth();
  const { t, locale } = useI18n();
  const nav = useNavigate();

  // Capture ?ref=CODE at mount so a referral link like /register/player?ref=ABC
  // (and the get-started forwarder) attributes the signup even if the user
  // navigates around before submitting.
  useEffect(() => {
    const r = (params.get("ref") || "").trim().toUpperCase();
    if (r) setForm((f) => ({ ...f, ref: r }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ageConfirmed) { toast.error("You must confirm your age"); return; }
    setBusy(true);
    try {
      const u = await registerPlayer({ ...form, locale, ref: form.ref || undefined });
      toast.success("Account created. Please complete onboarding.");
      nav(u.onboardingComplete ? "/player" : "/onboarding");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Registration failed"));
    } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  return (
    <div className="max-w-lg mx-auto px-6 py-16 lg:py-24">
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7] mb-3">{t("auth.playerSignupEyebrow")}</div>
      <h1 className="font-display text-4xl font-black tracking-tighter mb-4">{t("auth.registerPlayer")}</h1>
      <p className="text-sm text-[#8B97AE] mb-8">{t("auth.playerSignupDesc")}</p>
      <form onSubmit={submit} className="space-y-5" data-testid="register-player-form">
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("auth.alias")}</Label>
          <Input data-testid="rp-alias" required minLength={2} value={form.alias} onChange={set("alias")} className="rounded-none bg-transparent border-white/20 h-12 mt-2 focus:ring-2 focus:ring-[#2DD4A7]" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("auth.email")}</Label>
          <Input data-testid="rp-email" type="email" required value={form.email} onChange={set("email")} className="rounded-none bg-transparent border-white/20 h-12 mt-2 focus:ring-2 focus:ring-[#2DD4A7]" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("auth.password")}</Label>
          <Input data-testid="rp-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="rounded-none bg-transparent border-white/20 h-12 mt-2 focus:ring-2 focus:ring-[#2DD4A7]" />
        </div>
        <label className="flex items-start gap-3 pt-2">
          <Checkbox data-testid="rp-age" checked={form.ageConfirmed} onCheckedChange={(v) => setForm((f) => ({ ...f, ageConfirmed: !!v }))} className="mt-0.5 rounded-none border-white/40 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-[#04150F]" />
          <span className="text-sm text-[#8B97AE]">{t("auth.ageConfirm")}</span>
        </label>
        {form.ref && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]" data-testid="rp-ref-notice">
            Referred by code · <span className="text-[#E8ECF4]">{form.ref}</span>
          </div>
        )}
        <Button data-testid="rp-submit" type="submit" disabled={busy} className="w-full rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-12 font-semibold transition-colors">
          {busy ? t("common.loading") : t("auth.registerPlayer")}
        </Button>
      </form>
      <div className="mt-6 text-sm text-[#8B97AE]">
        {t("auth.haveAccount")}{" "}
        <Link to="/login" className="text-[#E8ECF4] underline hover:text-[#2DD4A7]" data-testid="rp-goto-login">{t("nav.login")}</Link>
      </div>
    </div>
  );
}
